import { useState } from "react";
import { api } from "@/lib/api";
import { useSciPreview } from "@/components/sci/useSciPreview";

const STATUS = { needs_review: "Needs review", linked_staged: "Linked (staged)", no_official_link: "No official match", rejected_no_official_link: "Rejected", all: "All" };

function Candidates({ r, pick, setPick }) {
  if (!r.candidate_details?.length) return <span className="text-stone-400">none</span>;
  return (
    <div className="space-y-1">
      {r.candidate_details.map((c) => (
        <label key={c.kis_ref} className="flex items-start gap-1.5">
          <input type="radio" name={`pick-${r.id}`} data-testid={`cw-pick-${r.id}-${c.kis_ref}`} checked={pick === c.kis_ref} onChange={() => setPick(c.kis_ref)} className="mt-0.5" />
          <span>
            <span className="font-medium">{c.title}</span> · {c.award} · mode {c.modes.join("/")}
            {c.variant.length ? ` · ${c.variant.join(", ").toLowerCase()}` : ""}{c.has_leo5 ? " · LEO5" : ""}
            {r.suggested === c.kis_ref && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">suggested</span>}
            <code className="block break-all text-[10px] text-stone-400">{c.kis_ref}{c.ucas.length ? ` · UCAS ${c.ucas.join(" ")}` : ""}</code>
            {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-[10px] underline">course page</a>}
          </span>
        </label>
      ))}
    </div>
  );
}

function Row({ r, onDone }) {
  const [pick, setPick] = useState(r.suggested || "");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState(null);
  const send = (decision) => api.post("/admin/official-data/crosswalk/decision", { degree_id: r.id, decision, kis_ref: decision === "approve" ? pick : null, note })
    .then(() => { setMsg(`${decision} recorded`); onDone(); }).catch((e) => setMsg(e.response?.data?.detail || "failed"));
  const d = r.pending_decision;
  return (
    <tr data-testid={`cw-row-${r.id}`} className="border-b border-stone-100 align-top">
      <td className="px-2 py-2"><code className="block min-w-[150px] break-all text-[10px]">{r.id}</code><span className="font-medium">{r.title}</span><div className="text-stone-500">{r.qualification} · {r.university_id}{r.ucas_course_code ? ` · UCAS ${r.ucas_course_code}` : ""}</div>
        <div className="text-stone-400">shortlists {r.shortlist_refs} · {r.editorial?.narrated ? "editorial" : "no editorial"}</div></td>
      <td className="px-2 py-2"><span className="font-medium">{r.class.replace(/_/g, " ")}</span><div className="text-stone-500">{r.cause}</div><div className="text-stone-400">provider: {r.evidence?.provider_status}</div></td>
      <td className="px-2 py-2"><Candidates r={r} pick={pick} setPick={setPick} /></td>
      <td className="px-2 py-2 min-w-[180px]">
        {d && <p data-testid={`cw-decision-${r.id}`} className="mb-1 text-[10px] text-emerald-700">Recorded: {d.decision}{d.kis_ref ? ` ${d.kis_ref}` : ""} by {d.by}</p>}
        <input data-testid={`cw-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (required)" className="w-full rounded border border-stone-300 px-1.5 py-1 text-[11px]" />
        <div className="mt-1 flex gap-1">
          <button data-testid={`cw-approve-${r.id}`} disabled={!pick || !note.trim()} onClick={() => send("approve")} className="rounded bg-emerald-700 px-2 py-0.5 text-[11px] text-white disabled:opacity-40">Approve link</button>
          <button data-testid={`cw-reject-${r.id}`} disabled={!note.trim()} onClick={() => send("reject")} className="rounded border border-red-300 px-2 py-0.5 text-[11px] text-red-700 disabled:opacity-40">No match</button>
          <button data-testid={`cw-defer-${r.id}`} disabled={!note.trim()} onClick={() => send("defer")} className="rounded border px-2 py-0.5 text-[11px] disabled:opacity-40">Defer</button>
        </div>
        {msg && <p data-testid={`cw-msg-${r.id}`} className="mt-1 text-[10px] text-stone-500">{msg}</p>}
      </td>
    </tr>
  );
}

export const CrosswalkReview = () => {
  const [status, setStatus] = useState("needs_review");
  const [cls, setCls] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [tick, setTick] = useState(0);
  const sum = useSciPreview("/admin/official-data/crosswalk");
  const { data } = useSciPreview(`/admin/official-data/crosswalk/review?status=${status}&limit=20&offset=${page * 20}${cls ? `&cls=${cls}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}&t=${tick}`);
  const s = sum.data?.crosswalk;
  const v = sum.data?.earnings_release?.validation;
  return (
    <section data-testid="di-crosswalk" className="mt-8 rounded-xl border border-stone-200 bg-white p-5">
      <h2 className="font-editorial text-2xl text-stone-900">Curated courses · official course matching</h2>
      {s && (
        <div className="mt-3 text-sm text-stone-700">
          <p data-testid="cw-summary">{s.records.toLocaleString()} curated UK courses · crosswalk <code>{s.version}</code> · {Object.entries(s.classes).map(([k, n]) => `${k.replace(/_/g, " ")} ${n}`).join(" · ")}</p>
          <p className="mt-1 text-xs">Linked and showing official figures in this preview: <b data-testid="cw-linked">{s.linked}</b>. Everything else shows "Official data not available". Release QA: <b data-testid="cw-qa">{v ? (v.pass ? "passed" : "failed") : "—"}</b>. Provider codes corrected: {Object.keys(s.provider_corrections).length}; providers needing review: {Object.keys(s.providers_needing_review).length}.</p>
          <p className="mt-1 text-xs text-stone-500">Decisions are recorded only. They are applied at the next staged build and nothing goes live without the owner's sign-off.</p>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <select data-testid="cw-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} className="rounded border border-stone-300 px-2 py-1">
          {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="cw-class" value={cls} onChange={(e) => { setCls(e.target.value); setPage(0); }} className="rounded border border-stone-300 px-2 py-1">
          <option value="">All classes</option><option value="high_confidence_review">High-confidence review</option><option value="ambiguous">Ambiguous</option>
          <option value="deterministic_exact">Deterministic exact</option><option value="unmatched">Unmatched</option>
        </select>
        <input data-testid="cw-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search id, title, university" className="rounded border border-stone-300 px-2 py-1" />
        <button data-testid="cw-prev" disabled={!page} onClick={() => setPage((p) => p - 1)} className="rounded border px-2 disabled:opacity-40">Prev</button>
        <button data-testid="cw-next" disabled={!data || (page + 1) * 20 >= data.total} onClick={() => setPage((p) => p + 1)} className="rounded border px-2 disabled:opacity-40">Next</button>
        <span data-testid="cw-total" className="text-stone-500">{data ? `${data.total.toLocaleString()} courses` : "Loading…"}</span>
      </div>
      {data && (
        <div className="mt-3 overflow-x-auto">
          <table data-testid="cw-table" className="w-full min-w-[900px] text-left text-xs">
            <thead className="border-b border-stone-200 text-stone-500"><tr><th className="px-2 py-2">WUA course</th><th className="px-2 py-2">Match</th><th className="px-2 py-2">Official candidates</th><th className="px-2 py-2">Decision</th></tr></thead>
            <tbody>{data.rows.map((r) => <Row key={r.id} r={r} onDone={() => setTick((t) => t + 1)} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
};
